import React, { useEffect } from "react";
import { Chart, registerables } from "chart.js";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import { Chart as ReactChart, Bar, Line, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getColorByCount } from "../../../utils/getGraphColor";
Chart.register(...registerables, ChartDataLabels);

const GraphCard = ({
  title,
  data,
  type,
  gaugeValue,
  gaugeNeedleBasedOn = "width",
  style = {},
  cardHeight = 180,
}) => {
  const theme = useTheme();

  const getChartType = () => {
    if (type?.includes("bar")) return "bar";
    if (type?.includes("doughnut")) return "doughnut";
    if (type?.includes("line")) return "line";
    if (type?.includes("gauge")) return "gauge";
  };

  const getDataGraph = () => {
    const dataGraph = {
      datasets: [
        {
          data:
            type === "gauge"
              ? [13, 20, 17, 17, 20, 13]
              : data?.map((dt) => dt.countData),
          backgroundColor:
            type === "gauge"
              ? [
                  "#ff3e4d",
                  "#ff3e4e",
                  "#ffa207",
                  "#ffa300",
                  "#80b800",
                  "#80bd01",
                ]
              : getColorByCount(data?.length),
          borderRadius: 4,
          categoryPercentage: 0.5,
        },
      ],
      labels: data?.map((dt) => dt.groupName),
    };
    let newDatasets = { ...dataGraph.datasets[0] };
    let newLabels = dataGraph.labels;
    switch (type) {
      case "barHorizontalLabelInside":
        newLabels = data?.map(() => " ");
        newDatasets = {
          ...newDatasets,
          barThickness: 25,
          borderRadius: 4,
          categoryPercentage: 0.5,
        };
        break;
      case "barHorizontal":
      case "barHorizontalPercentage":
        newDatasets = {
          ...newDatasets,
          barThickness: 20,
          borderRadius: 4,
          categoryPercentage: 0.5,
        };
        break;
      case "barVertical":
        // newLabels = data?.map((dt) => dt.groupName);
        newDatasets = {
          ...newDatasets,
          barThickness: 30,
          borderRadius: 4,
          categoryPercentage: 0.5,
        };
        break;
      case "line":
        newDatasets = {
          ...newDatasets,
          fill: false,
          tension: 0.1,
        };
        break;
      case "gauge":
        newDatasets = {
          ...newDatasets,
          needlevalue: gaugeValue,
        };
        break;
    }
    return {
      datasets: Array.isArray(newDatasets) ? newDatasets : [newDatasets],
      labels: newLabels,
    };
  };

  const getOptions = () => {
    let opt = {
      animation: false,
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
      },
    };
    switch (type) {
      case "barHorizontalLabelInside":
        opt = {
          ...opt,
          cornerRadius: 20,
          layout: { padding: 0 },
          type: "bar",
          indexAxis: "y",
          yAxes: [
            {
              ticks: {
                fontColor: theme.palette.text.secondary,
                beginAtZero: true,
                min: 0,
              },
              gridLines: {
                borderDash: [2],
                borderDashOffset: [2],
                color: theme.palette.divider,
                drawBorder: false,
                zeroLineBorderDash: [2],
                zeroLineBorderDashOffset: [2],
                zeroLineColor: theme.palette.divider,
              },
            },
          ],
          scales: {
            x: {
              ticks: {
                display: false,
              },
            },
          },
          plugins: {
            ...opt.plugins,
            datalabels: {
              display: true,
              anchor: "start",
              align: "right",
              formatter: function (value, context) {
                return data[context.dataIndex].groupName;
              },
            },
            tooltip: {
              callbacks: {
                title: function (value, context) {
                  return data[value[0].dataIndex].groupName;
                },
              },
            },
          },
        };
        break;
      case "barHorizontal":
        opt = {
          ...opt,
          cornerRadius: 20,
          layout: { padding: 0 },
          type: "bar",
          indexAxis: "y",
          yAxes: [
            {
              ticks: {
                fontColor: theme.palette.text.secondary,
                beginAtZero: true,
                min: 0,
              },
              gridLines: {
                borderDash: [2],
                borderDashOffset: [2],
                color: theme.palette.divider,
                drawBorder: false,
                zeroLineBorderDash: [2],
                zeroLineBorderDashOffset: [2],
                zeroLineColor: theme.palette.divider,
              },
            },
          ],
          scales: {
            x: {
              ticks: {
                display: false,
              },
            },
          },
          plugins: {
            ...opt.plugins,
            datalabels: {
              display: true,
              anchor: "center",
              align: "right",
              formatter: function (value, context) {
                return data[context.dataIndex].countData;
              },
            },
          },
        };
        break;
      case "barHorizontalPercentage":
        opt = {
          ...opt,
          cornerRadius: 20,
          layout: { padding: 0 },
          type: "bar",
          indexAxis: "y",
          yAxes: [
            {
              ticks: {
                fontColor: theme.palette.text.secondary,
                beginAtZero: true,
                min: 0,
              },
              gridLines: {
                borderDash: [2],
                borderDashOffset: [2],
                color: theme.palette.divider,
                drawBorder: false,
                zeroLineBorderDash: [2],
                zeroLineBorderDashOffset: [2],
                zeroLineColor: theme.palette.divider,
              },
            },
          ],
          scales: {
            x: {
              ticks: {
                display: false,
              },
            },
          },
          plugins: {
            ...opt.plugins,
            datalabels: {
              display: true,
              anchor: "center",
              align: "right",
              formatter: function (value, context) {
                return data[context.dataIndex].countData + "%";
              },
            },
            tooltip: {
              callbacks: {
                title: function (value, context) {
                  return data[value[0].dataIndex].groupName;
                },
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.c !== null) {
                    label += `${context.parsed.x}%`;
                  }
                  return label;
                },
              },
            },
          },
        };
        break;
      case "barVertical":
        opt = {
          ...opt,
          cornerRadius: 20,
          layout: { padding: 0 },
          type: "bar",
          indexAxis: "x",
          xAxes: [
            {
              ticks: {
                fontColor: theme.palette.text.secondary,
                beginAtZero: true,
                min: 0,
              },
              gridLines: {
                borderDash: [2],
                borderDashOffset: [2],
                color: theme.palette.divider,
                drawBorder: false,
                zeroLineBorderDash: [2],
                zeroLineBorderDashOffset: [2],
                zeroLineColor: theme.palette.divider,
              },
            },
          ],
          scales: {
            y: {
              ticks: {
                display: false,
                beginAtZero: true,
              },
            },
            x: {
              ticks: {
                display: false,
              },
            },
          },
          plugins: {
            ...opt.plugins,
            legend: {
              display: true,
              position: "bottom",
              labels: {
                generateLabels: (chart) => {
                  return chart.data.labels.map((label, idx) => ({
                    text: label,
                    fillStyle: chart.data.datasets[0].backgroundColor[idx],
                    strokeStyle: chart.data.datasets[0].backgroundColor[idx],
                  }));
                },
              },
            },
            datalabels: {
              display: true,
              anchor: "center",
              align: "top",
              formatter: function (value, context) {
                return data[context.dataIndex].countData;
              },
            },
          },
        };
        break;
      case "barVerticalPercentage":
        opt = {
          ...opt,
          cornerRadius: 20,
          layout: { padding: 0 },
          type: "bar",
          indexAxis: "x",
          xAxes: [
            {
              ticks: {
                fontColor: theme.palette.text.secondary,
                beginAtZero: true,
                min: 0,
              },
              gridLines: {
                borderDash: [2],
                borderDashOffset: [2],
                color: theme.palette.divider,
                drawBorder: false,
                zeroLineBorderDash: [2],
                zeroLineBorderDashOffset: [2],
                zeroLineColor: theme.palette.divider,
              },
            },
          ],
          scales: {
            y: {
              ticks: {
                display: false,
                beginAtZero: true,
              },
            },
            x: {
              ticks: {
                display: false,
              },
            },
          },
          plugins: {
            ...opt.plugins,
            legend: {
              display: true,
              position: "bottom",
              labels: {
                generateLabels: (chart) => {
                  return chart.data.labels.map((label, idx) => ({
                    text: label,
                    fillStyle: chart.data.datasets[0].backgroundColor[idx],
                    strokeStyle: chart.data.datasets[0].backgroundColor[idx],
                  }));
                },
              },
            },
            datalabels: {
              display: true,
              anchor: "center",
              align: "top",
              formatter: function (value, context) {
                return data[context.dataIndex].countData + "%";
              },
            },
            tooltip: {
              callbacks: {
                title: function (value, context) {
                  return data[value[0].dataIndex].groupName;
                },
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.c !== null) {
                    label += `${context.parsed.y}%`;
                  }
                  return label;
                },
              },
            },
          },
        };
        break;
      case "doughnut":
        opt = {
          ...opt,
          weight: 0.4,
          cutout: 10,
          maintainAspectRatio: false,
          plugins: {
            ...opt.plugins,
            datalabels: {
              display: true,
              formatter: function (value, context) {
                return value === 0
                  ? null
                  : `${data[context.dataIndex].groupName} : ${value}`;
              },
            },
          },
        };
        break;
      case "doughnutPercentage":
        opt = {
          ...opt,
          weight: 0.4,
          cutout: 10,
          maintainAspectRatio: false,
          plugins: {
            ...opt.plugins,
            datalabels: {
              display: true,
              formatter: function (value, context) {
                return value === 0
                  ? null
                  : `${data[context.dataIndex].groupName} : ${value}%`;
              },
            },
            tooltip: {
              callbacks: {
                title: function (value, context) {
                  return data[value[0].dataIndex].groupName;
                },
                label: function (context) {
                  return ` ${context.dataset.data[context.dataIndex]}%`;
                },
              },
            },
          },
        };
        break;
      case "line":
        opt = {
          ...opt,
          scales: {
            y: {
              ticks: {
                display: false,
                beginAtZero: true,
              },
            },
          },
        };
        break;
      case "gauge":
        opt = {
          ...opt,
          cutout: 65,
          circumference: 180,
          rotation: 270,
          plugins: {
            ...opt.plugins,
            tooltip: {
              enabled: false,
            },
          },
        };

        break;
    }
    return opt;
  };

  // custom plugin to show needle on gauge graph
  const gaugeNeedle = {
    id: "gaugeNeedle",
    afterDatasetDraw(chart, args, options) {
      const {
        ctx,
        config,
        data,
        chartArea: { top, bottom, left, right, width, height },
      } = chart;
      ctx.save();
      const needleValue = data.datasets[0].needlevalue; // percentage value
      const dataTotal = 100; // percentage total
      const angle =
        Math.PI +
        (1 / dataTotal) * (needleValue > 100 ? 100 : needleValue) * Math.PI;
      const cx = width / 2;
      const cy = chart._metasets[0].data[0].y - 10;

      // needle
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.moveTo(0, -2);
      const needleLength =
        gaugeNeedleBasedOn === "width" ? width / 2 - 20 : height / 2 + 70;
      ctx.lineTo(needleLength, 0);
      ctx.lineTo(0, 2);
      ctx.fillStyle = "#444";
      ctx.fill();

      // needle dot
      ctx.translate(-cx, -cy);
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, 10);
      ctx.fill();
      ctx.restore();

      // text
      ctx.font = "30px Helvetica";
      ctx.fillStyle = "#444";
      const percentageText = `${needleValue}%`;
      ctx.fillText(percentageText, cx - percentageText.length - 15, cy - 40);
    },
  };

  return (
    <Card sx={{ paddingTop: 1 }}>
      <Typography
        sx={{
          fontSize: 17,
          textAlign: "center",
          margin: "0 8px",
        }}
      >
        {title}
      </Typography>
      <CardContent>
        <Box
          sx={{
            height: cardHeight,
            display: "flex",
            flexDirection: "column-reverse",
            ...style,
          }}
        >
          {getChartType() === "bar" ? (
            <Bar data={getDataGraph()} options={getOptions()} />
          ) : null}
          {getChartType() === "doughnut" ? (
            <Doughnut data={getDataGraph()} options={getOptions()} />
          ) : null}
          {getChartType() === "line" ? (
            <Line data={getDataGraph()} options={getOptions()} />
          ) : null}
          {getChartType() === "gauge" ? (
            <Doughnut
              data={getDataGraph()}
              options={getOptions()}
              plugins={[gaugeNeedle]}
            />
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

export default GraphCard;
